import { ChartDataPoint, DataLayer } from 'features/systemLens'
import { COMPUTED_PEN_PREFIX } from 'features/timeSeries/computed/computedTrendStore'
import { DEFAULT_CHART_SETTINGS } from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import {
  predictValues,
  RegressionResult,
} from 'features/timeSeries/regressionHelpers'
import {
  PenCompanion,
  penCompanionLabel,
} from 'features/timeSeries/utils/penCompanions'
import {
  holdLastSampleToEnd,
  isSteppedSensor,
} from 'features/timeSeries/utils/steppedPens'
import {
  EnhancedSensorEntry,
  RegressionConfig,
  SensorAxisMapping,
  TimeSeriesDerivedValueEntryJSON,
} from 'types'
import type uPlot from 'uplot'

import { resampleData } from './dataProcessing'

interface UPlotDataOptions {
  dataLayers: DataLayer[]
  chartData: ChartDataPoint[]
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  unitMapping: Record<string, string>
  sensorAxisMapping: SensorAxisMapping
  regressionConfigs: RegressionConfig[]
  regressionResults: Record<string, RegressionResult>
  derivedValues: TimeSeriesDerivedValueEntryJSON[]
  xAxisSensorId?: string | null
  connectPoints: boolean
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  highlightedSensorId?: string | null
  secondaryHighlightedSensorIds?: string[]
  annotationProtectedSensorIds?: string[]
  /** Sensors rendered de-emphasized (dotted + translucent), e.g. related ones. */
  deemphasizedSensorIds?: string[]
  singleScaleMode: boolean
  /** Rolling-average / training-bucket views of pens already on the chart. */
  penCompanions?: PenCompanion[]
  /**
   * `uPlot.paths.stepped` for bucket lines and stepped pens. Injected by the
   * caller to keep this module's uplot import type-only, since loading uplot
   * here would pull its `matchMedia` call into node-environment tests.
   */
  steppedPaths?: UPlotSeriesConfig['paths']
  /** Timestamps a filter or the cleaning plan dropped, per pen. */
  excludedBySensor?: Map<string, number[]>
  computedGapsBySensor?: Map<string, Array<[number, number]>>
  computedScalePairs?: Map<string, string>
  /** Periods an agent model's gate or masked ranges removed, for every pen. */
  droppedRanges?: Array<[number, number]>
  /** Periods to cut from particular pens only: the model's own pens across
   *  holes it never graded, so they break where the band breaks. */
  droppedRangesBySensor?: Map<string, Array<[number, number]>>
}

/**
 * Extended uPlot series config with sensor metadata.
 * Properties from uPlot.Series are explicitly redeclared because tsgo
 * may not resolve the uPlot namespace types when using `extends`.
 */
export interface UPlotSeriesConfig extends uPlot.Series {
  label?: string | HTMLElement
  scale?: string
  show?: boolean
  stroke?: string
  fill?: string
  width?: number
  alpha?: number
  spanGaps?: boolean
  dash?: number[]
  points?: { show?: boolean; size?: number; fill?: string }
  _sensorId?: string
  _isRegression?: boolean
  /** Identifies the exclusions baked into `gaps`, for the series structural diff. */
  _excludedDigest?: number
  _stepped?: boolean
}

/**
 * Order-sensitive checksum of the gap inputs a series' `gaps` closure bakes in
 * (same role as `removedPointsDigest`). A count is not enough: spans that
 * shift after a refit, or a list pinned at the coalescing cap, keep the same
 * count while the closure goes stale.
 */
export const excludedGapsDigest = (
  excludedXs: number[],
  ranges: Array<[number, number]>
): number => {
  const bits = new DataView(new ArrayBuffer(8))
  let hash = 0
  const mix = (value: number) => {
    bits.setFloat64(0, value)
    hash = Math.imul(hash ^ bits.getInt32(0), 2246822519)
    hash = Math.imul(hash ^ bits.getInt32(4), 2654435761)
  }
  mix(excludedXs.length)
  for (const x of excludedXs) mix(x)
  for (const [from, to] of ranges) {
    mix(from)
    mix(to)
  }
  return hash
}

interface AlignedColumnsOptions {
  sensorsToPlot: Array<{ id: string; name: string }>
  sortedX: number[]
  sensorLookups: Map<string, Map<number, number | null>>
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  highlightedSensorId?: string | null
  secondaryHighlightedSensorIds?: string[]
  annotationProtectedSensorIds?: string[]
  deemphasizedSensorIds?: string[]
  connectPoints: boolean
  enhancedSensorEntries: EnhancedSensorEntry[]
  sensorAxisMapping: SensorAxisMapping
  singleScaleMode: boolean
  excludedBySensor?: Map<string, number[]>
  computedGapsBySensor?: Map<string, Array<[number, number]>>
  computedScaleBySensor?: Map<string, string>
  droppedRanges?: Array<[number, number]>
  droppedRangesBySensor?: Map<string, Array<[number, number]>>
  steppedPaths?: UPlotSeriesConfig['paths']
  isTimeAxis: boolean
}

/**
 * The dropped timestamps as pixel gaps, so the line breaks where the data went
 * instead of being drawn straight over it.
 *
 * uPlot only consults this with `spanGaps` off, and the null-derived gaps it
 * hands in are dropped: on the unified x axis a pen reads null wherever only
 * another pen sampled, and breaking on those would shred every line.
 */
export const excludedGapRefiner =
  (
    excludedXs: number[],
    /**
     * Whole [start, end) ranges to cut, for a filter that removes a PERIOD
     * rather than scattered samples.
     *
     * A per-sample gap is a pixel wide, which is right for a threshold filter
     * dropping the odd reading. It is wrong for a gated-out shutdown: the
     * dropped rows are absent from the series, so the path already runs straight
     * from the last kept point to the first one after, and punching a couple of
     * pixels at each removed sample leaves nearly all of that bridge drawn — the
     * data is gone and the chart still shows a line across it. A range cuts the
     * bridge over its whole width, which is what makes the period read as
     * absent.
     */
    ranges: Array<[number, number]> = []
  ): UPlotSeriesConfig['gaps'] =>
  (u) => [
    ...excludedXs.map((x): [number, number] => {
      const px = u.valToPos(x, 'x', true)
      return [px - devicePixelRatio, px + devicePixelRatio]
    }),
    ...ranges.map((range): [number, number] => [
      u.valToPos(range[0], 'x', true),
      u.valToPos(range[1], 'x', true),
    ]),
  ]

/**
 * Gap refiner for computed pens: bridge null-derived gaps from row
 * interleaving, but hand the pen's own explicitly-null spans (gated-out
 * intervals) back as real gaps.
 */
export const rangeGapRefiner =
  (ranges: Array<[number, number]>): UPlotSeriesConfig['gaps'] =>
  (u) =>
    ranges.map(([start, end]) => [
      u.valToPos(start, 'x', true),
      u.valToPos(end, 'x', true),
    ])

/** Explicit-null spans of a computed pen, widened half a step each side. */
export const computedNullRanges = (
  chartData: ChartDataPoint[],
  sensorId: string
): Array<[number, number]> => {
  const samples: Array<{ x: number; isNull: boolean }> = []
  chartData.forEach((point) => {
    const value = point[sensorId]
    if (value === undefined) return
    samples.push({ x: point.timestamp, isNull: value === null })
  })
  if (samples.length < 2) return []
  let step = Infinity
  for (let i = 1; i < samples.length; i++) {
    const diff = samples[i].x - samples[i - 1].x
    if (diff > 0 && diff < step) step = diff
  }
  if (!Number.isFinite(step)) return []
  const ranges: Array<[number, number]> = []
  let runStart: number | null = null
  let runEnd = 0
  samples.forEach(({ x, isNull }) => {
    if (isNull) {
      if (runStart === null) runStart = x
      runEnd = x
      return
    }
    if (runStart !== null) {
      ranges.push([runStart - step / 2, runEnd + step / 2])
      runStart = null
    }
  })
  if (runStart !== null) ranges.push([runStart - step / 2, runEnd + step / 2])
  return ranges
}

function buildSensorValueMaps(
  sensorsToPlot: Array<{ id: string }>,
  chartData: ChartDataPoint[],
  xAxisSensorId: string | null | undefined
): {
  sensorXValues: Map<string, (number | null)[]>
  sensorYValues: Map<string, (number | null)[]>
} {
  const sensorXValues = new Map<string, (number | null)[]>()
  const sensorYValues = new Map<string, (number | null)[]>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xVals: (number | null)[] = []
    const yVals: (number | null)[] = []

    if (xAxisSensorId) {
      chartData.forEach((point) => {
        const xValue = point[xAxisSensorId]
        const yValue = point[sensorId]
        if (typeof xValue === 'number' && typeof yValue === 'number') {
          xVals.push(xValue)
          yVals.push(yValue)
        }
      })
    } else {
      chartData.forEach((point) => {
        if (
          point.timestamp !== null &&
          point.timestamp !== undefined &&
          typeof point[sensorId] === 'number'
        ) {
          xVals.push(point.timestamp)
          yVals.push(point[sensorId] as number)
        }
      })
    }

    const { x: resampledX, y: resampledY } = resampleData(
      xVals,
      yVals,
      DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS
    )

    sensorXValues.set(sensorId, resampledX)
    sensorYValues.set(sensorId, resampledY)
  })

  return { sensorXValues, sensorYValues }
}

function buildUnifiedXAxis(
  sensorsToPlot: Array<{ id: string }>,
  sensorXValues: Map<string, (number | null)[]>,
  companionSeries: CompanionSeriesData[] = []
): number[] {
  const xSet = new Set<number>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xs = sensorXValues.get(sensorId) || []
    xs.forEach((x) => {
      if (x !== null) xSet.add(x)
    })
  })

  // Bucket starts rarely coincide with a pen's sample times, so companions
  // contribute their own x values; pens read null at the rest.
  companionSeries.forEach(({ lookup }) =>
    lookup.forEach((_value, x) => xSet.add(x))
  )

  return Array.from(xSet).sort((a, b) => a - b)
}

function buildSensorLookups(
  sensorsToPlot: Array<{ id: string }>,
  sensorXValues: Map<string, (number | null)[]>,
  sensorYValues: Map<string, (number | null)[]>
): Map<string, Map<number, number | null>> {
  const sensorLookups = new Map<string, Map<number, number | null>>()

  sensorsToPlot.forEach(({ id: sensorId }) => {
    const xs = sensorXValues.get(sensorId) || []
    const ys = sensorYValues.get(sensorId) || []
    const lookup = new Map<number, number | null>()
    xs.forEach((x, i) => {
      if (x !== null) lookup.set(x, ys[i])
    })
    sensorLookups.set(sensorId, lookup)
  })

  return sensorLookups
}

function buildAlignedColumns(options: AlignedColumnsOptions): {
  yColumns: (number | null | undefined)[][]
  seriesAdditions: UPlotSeriesConfig[]
} {
  const {
    sensorsToPlot,
    sortedX,
    sensorLookups,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    connectPoints,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
    excludedBySensor,
    computedGapsBySensor,
    computedScaleBySensor,
    droppedRanges,
    droppedRangesBySensor,
    steppedPaths,
    isTimeAxis,
  } = options

  const deemphasizedSet = new Set(deemphasizedSensorIds ?? [])

  const hasHighlight =
    highlightedSensorId !== null ||
    (secondaryHighlightedSensorIds?.length ?? 0) > 0
  const yColumns: (number | null | undefined)[][] = []
  const seriesAdditions: UPlotSeriesConfig[] = []

  sensorsToPlot.forEach(({ id: sensorId, name }, index) => {
    const lookup = sensorLookups.get(sensorId)!
    const yCol: (number | null | undefined)[] = sortedX.map(
      (x) => lookup.get(x) ?? null
    )

    const visible = sensorVisibilityConfig?.[sensorId] ?? true
    const lineColor = customSensorColors?.[sensorId] || getSensorColor(index)
    const isHighlighted = highlightedSensorId === sensorId
    const isSecondaryHighlighted =
      secondaryHighlightedSensorIds?.includes(sensorId) ?? false
    const isAnnotationProtected =
      annotationProtectedSensorIds?.includes(sensorId) ?? false
    const isDeemphasized = deemphasizedSet.has(sensorId)
    const opacity = hasHighlight
      ? isHighlighted
        ? 1
        : isSecondaryHighlighted
          ? 0.72
          : isAnnotationProtected
            ? 1
            : 0.24
      : isDeemphasized
        ? 0.4
        : 1
    const lineWidth = connectPoints
      ? isHighlighted
        ? 1.9
        : isSecondaryHighlighted
          ? 1.45
          : 1.2
      : 0
    const markerSize = connectPoints ? 0 : isHighlighted ? 6 : 4

    const isComputed = sensorId.startsWith(COMPUTED_PEN_PREFIX)
    const isDerived =
      sensorId.startsWith('derived_') ||
      sensorId.startsWith('regression_') ||
      isComputed
    const sensor = isDerived
      ? undefined
      : enhancedSensorEntries.find((e) => e.id === sensorId)
    const displayName = isDerived
      ? name || sensorId
      : sensor?.data?.name || sensorId
    const isStepped = isSteppedSensor(sensor?.data)
    const drawsSteppedLine = isStepped && connectPoints && steppedPaths
    yColumns.push(
      drawsSteppedLine && isTimeAxis ? holdLastSampleToEnd(yCol) : yCol
    )

    const pairedScale = isComputed
      ? computedScaleBySensor?.get(sensorId)
      : undefined
    const scaleKey = singleScaleMode
      ? 'y'
      : (pairedScale ?? (sensorAxisMapping[sensorId] ? sensorId : 'y'))

    const excludedXs = excludedBySensor?.get(sensorId) ?? []
    const cutRanges = [
      ...(droppedRanges ?? []),
      ...(droppedRangesBySensor?.get(sensorId) ?? []),
    ]

    seriesAdditions.push({
      label: displayName,
      scale: scaleKey,
      show: visible,
      stroke: lineColor,
      width: lineWidth,
      alpha: opacity,
      // Gaps are only honoured with spanGaps off; the refiner puts back the
      // bridging this pen still needs across the unified axis. Computed pens
      // never bridge: their nulls are deliberate.
      ...(isComputed
        ? {
            spanGaps: false,
            gaps: rangeGapRefiner(computedGapsBySensor?.get(sensorId) ?? []),
          }
        : excludedXs.length > 0 || cutRanges.length > 0
          ? {
              spanGaps: false,
              gaps: excludedGapRefiner(excludedXs, cutRanges),
              _excludedDigest: excludedGapsDigest(excludedXs, cutRanges),
            }
          : { spanGaps: true }),
      ...(isDeemphasized ? { dash: [2, 4] } : {}),
      ...(isStepped && steppedPaths ? { paths: steppedPaths } : {}),
      points: {
        show: !connectPoints,
        size: markerSize,
        fill: lineColor,
      },
      _sensorId: sensorId,
      _stepped: isStepped,
    })
  })

  return { yColumns, seriesAdditions }
}

/**
 * Builds a unified timestamp array and aligned y-columns from ChartDataPoint[]
 */
export function generateUPlotData(options: UPlotDataOptions): {
  data: uPlot.AlignedData
  series: UPlotSeriesConfig[]
} {
  const {
    dataLayers,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    sensorAxisMapping,
    xAxisSensorId,
    connectPoints,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    singleScaleMode,
    regressionConfigs,
    regressionResults,
    derivedValues,
    penCompanions,
    steppedPaths,
    excludedBySensor,
    computedScalePairs,
    droppedRanges,
    droppedRangesBySensor,
  } = options

  const computedGapsBySensor = new Map<string, Array<[number, number]>>()
  allSensorsToRender.forEach(({ id }) => {
    if (!id.startsWith(COMPUTED_PEN_PREFIX)) return
    computedGapsBySensor.set(id, computedNullRanges(chartData, id))
  })

  // Pair only when the base pen is actually mapped on this chart.
  const computedScaleBySensor = new Map<string, string>()
  computedScalePairs?.forEach((baseId, computedId) => {
    if (sensorAxisMapping[baseId]) computedScaleBySensor.set(computedId, baseId)
  })

  const processDataLayer = dataLayers.find((l) => l.id === 'process-data')
  const seriesConfigs: UPlotSeriesConfig[] = [{}]

  if (!processDataLayer?.enabled || chartData.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const sensorsToPlot = allSensorsToRender.filter((s) => s.id !== xAxisSensorId)

  if (sensorsToPlot.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const { sensorXValues, sensorYValues } = buildSensorValueMaps(
    sensorsToPlot,
    chartData,
    xAxisSensorId
  )

  // Companions are keyed by timestamp, so they only align in time mode.
  const companionSeries = xAxisSensorId
    ? []
    : buildCompanionSeriesData(penCompanions ?? [], chartData)

  const sortedX = buildUnifiedXAxis(
    sensorsToPlot,
    sensorXValues,
    companionSeries
  )

  if (sortedX.length === 0) {
    return { data: [[]], series: seriesConfigs }
  }

  const sensorLookups = buildSensorLookups(
    sensorsToPlot,
    sensorXValues,
    sensorYValues
  )

  const { yColumns, seriesAdditions } = buildAlignedColumns({
    sensorsToPlot,
    sortedX,
    sensorLookups,
    sensorVisibilityConfig,
    customSensorColors,
    highlightedSensorId,
    secondaryHighlightedSensorIds,
    annotationProtectedSensorIds,
    deemphasizedSensorIds,
    connectPoints,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
    excludedBySensor,
    computedGapsBySensor,
    computedScaleBySensor,
    droppedRanges,
    droppedRangesBySensor,
    steppedPaths,
    isTimeAxis: !xAxisSensorId,
  })

  seriesAdditions.forEach((s) => seriesConfigs.push(s))

  const regressionData = generateRegressionColumns(
    regressionConfigs,
    regressionResults,
    chartData,
    xAxisSensorId ?? undefined,
    sensorAxisMapping,
    allSensorsToRender,
    enhancedSensorEntries,
    derivedValues,
    singleScaleMode,
    sortedX
  )

  regressionData.forEach(({ yColumn, series }) => {
    yColumns.push(yColumn)
    seriesConfigs.push(series)
  })

  generateCompanionColumns({
    companionSeries,
    sortedX,
    sensorsToPlot,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
    sensorVisibilityConfig,
    customSensorColors,
    connectPoints,
    steppedPaths,
  }).forEach(({ yColumn, series }) => {
    yColumns.push(yColumn)
    seriesConfigs.push(series)
  })

  const alignedData: uPlot.AlignedData = [sortedX, ...yColumns]
  return { data: alignedData, series: seriesConfigs }
}

interface CompanionSeriesData {
  companion: PenCompanion
  lookup: Map<number, number>
}

// Companion columns are decimated on the same budget as the pens, since every
// point they keep widens the shared x-axis and therefore every pen's column.
function buildCompanionSeriesData(
  companions: PenCompanion[],
  chartData: ChartDataPoint[]
): CompanionSeriesData[] {
  return companions.flatMap((companion) => {
    const xs: number[] = []
    const ys: (number | null)[] = []

    chartData.forEach((point) => {
      const value = point[companion.columnId]
      if (typeof value !== 'number') return
      xs.push(point.timestamp)
      ys.push(value)
    })

    if (xs.length === 0) return []

    const { x, y } = resampleData(
      xs,
      ys,
      DEFAULT_CHART_SETTINGS.RESAMPLE_MAX_POINTS
    )

    const lookup = new Map<number, number>()
    x.forEach((timestamp, index) => {
      const value = y[index]
      if (timestamp === null || value === null) return
      lookup.set(timestamp, value)
    })

    return lookup.size > 0 ? [{ companion, lookup }] : []
  })
}

interface CompanionColumnsOptions {
  companionSeries: CompanionSeriesData[]
  sortedX: number[]
  sensorsToPlot: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  sensorAxisMapping: SensorAxisMapping
  singleScaleMode: boolean
  sensorVisibilityConfig: Record<string, boolean>
  customSensorColors?: Record<string, string>
  connectPoints: boolean
  steppedPaths?: UPlotSeriesConfig['paths']
}

function generateCompanionColumns(options: CompanionColumnsOptions): Array<{
  yColumn: (number | null | undefined)[]
  series: UPlotSeriesConfig
}> {
  const {
    companionSeries,
    sortedX,
    sensorsToPlot,
    enhancedSensorEntries,
    sensorAxisMapping,
    singleScaleMode,
    sensorVisibilityConfig,
    customSensorColors,
    connectPoints,
    steppedPaths,
  } = options

  return companionSeries.flatMap(({ companion, lookup }) => {
    const penIndex = sensorsToPlot.findIndex(
      (sensor) => sensor.id === companion.parentSensorId
    )
    if (penIndex === -1) return []

    const parentId = companion.parentSensorId
    const lineColor = customSensorColors?.[parentId] || getSensorColor(penIndex)
    const displayName =
      enhancedSensorEntries.find((entry) => entry.id === parentId)?.data
        ?.name ||
      sensorsToPlot[penIndex].name ||
      parentId
    const scaleKey = singleScaleMode
      ? 'y'
      : sensorAxisMapping[parentId]
        ? parentId
        : 'y'

    // Buckets step rather than slope: the value is constant across the bucket
    // it summarizes, and it is labeled at the bucket start.
    const stepped = companion.kind === 'bucket' ? steppedPaths : undefined

    return [
      {
        yColumn: sortedX.map((x) => lookup.get(x) ?? null),
        series: {
          label: `${displayName} (${penCompanionLabel(companion.kind)})`,
          scale: scaleKey,
          show: sensorVisibilityConfig?.[parentId] ?? true,
          stroke: lineColor,
          width: 1.5,
          alpha: 0.85,
          spanGaps: true,
          ...(companion.kind === 'smoothed' ? { dash: [6, 3] } : {}),
          ...(stepped ? { paths: stepped } : {}),
          points: { show: !connectPoints, size: 3, fill: lineColor },
          _sensorId: parentId,
        } satisfies UPlotSeriesConfig,
      },
    ]
  })
}

interface RegressionColumn {
  yColumn: (number | null | undefined)[]
  series: UPlotSeriesConfig
}

interface RegressionSeriesOptions {
  config: RegressionConfig
  sensorDisplayName: string
  lineColor: string
  scaleKey: string
}

function buildRegressionSeries(
  options: RegressionSeriesOptions
): UPlotSeriesConfig {
  const { config, sensorDisplayName, lineColor, scaleKey } = options
  return {
    label: `${sensorDisplayName} (${config.type} fit)`,
    scale: scaleKey,
    show: true,
    stroke: lineColor,
    width: 1.2,
    dash: [6, 4],
    spanGaps: true,
    points: { show: false },
    _sensorId: config.yVariable,
    _isRegression: true,
  }
}

function buildXAxisRegressionColumn(
  config: RegressionConfig,
  regressionResult: RegressionResult,
  sortedX: number[],
  sensorDisplayName: string,
  lineColor: string,
  scaleKey: string
): RegressionColumn {
  const intercept = regressionResult.coefficients[0]
  const slope = regressionResult.coefficients[1]

  return {
    yColumn: sortedX.map((x) => intercept + slope * x),
    series: buildRegressionSeries({
      config,
      sensorDisplayName,
      lineColor,
      scaleKey,
    }),
  }
}

function buildTimestampRegressionColumn(
  config: RegressionConfig,
  regressionResult: RegressionResult,
  chartData: ChartDataPoint[],
  sortedX: number[],
  sensorDisplayName: string,
  lineColor: string,
  scaleKey: string
): RegressionColumn | null {
  const dataTimestamps = chartData
    .map((d) => d.timestamp)
    .filter((t): t is number => t !== null)

  if (dataTimestamps.length === 0) return null

  const minDataTime = Math.min(...dataTimestamps)
  const maxDataTime = Math.max(...dataTimestamps)
  const endTime = config.endDate
    ? new Date(config.endDate).getTime()
    : maxDataTime

  const numPoints = DEFAULT_CHART_SETTINGS.NUM_REGRESSION_POINTS
  const regressionTimestamps: number[] = []
  for (let i = 0; i <= numPoints; i++) {
    regressionTimestamps.push(
      minDataTime + (endTime - minDataTime) * (i / numPoints)
    )
  }

  const regressionValues = predictValues(
    regressionResult,
    config,
    regressionTimestamps
  )

  // Regression is computed over its own timestamp grid and must be interpolated
  // onto the unified x-axis since that axis cannot be modified after the fact.
  const yColumn: (number | null | undefined)[] = sortedX.map((x) => {
    if (x < minDataTime || x > endTime) return null

    let leftIdx = -1
    for (let i = regressionTimestamps.length - 1; i >= 0; i--) {
      if (regressionTimestamps[i] <= x) {
        leftIdx = i
        break
      }
    }

    if (leftIdx === -1) return regressionValues[0]
    if (leftIdx >= regressionTimestamps.length - 1)
      return regressionValues[regressionValues.length - 1]

    const t1 = regressionTimestamps[leftIdx]
    const t2 = regressionTimestamps[leftIdx + 1]
    const v1 = regressionValues[leftIdx]
    const v2 = regressionValues[leftIdx + 1]

    if (v1 === null || v2 === null) return null
    if (t2 === t1) return v1

    const ratio = (x - t1) / (t2 - t1)
    return v1 + ratio * (v2 - v1)
  })

  return {
    yColumn,
    series: buildRegressionSeries({
      config,
      sensorDisplayName,
      lineColor,
      scaleKey,
    }),
  }
}

function generateRegressionColumns(
  regressionConfigs: RegressionConfig[],
  regressionResults: Record<string, RegressionResult>,
  chartData: ChartDataPoint[],
  xAxisSensorId: string | undefined,
  sensorAxisMapping: SensorAxisMapping,
  allSensorsToRender: Array<{ id: string; name: string }>,
  enhancedSensorEntries: EnhancedSensorEntry[],
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  singleScaleMode: boolean,
  sortedX: number[]
): RegressionColumn[] {
  const result: RegressionColumn[] = []

  regressionConfigs.forEach((config) => {
    if (!config.enabled || !regressionResults[config.id]) return

    const regressionResult = regressionResults[config.id]
    const isDerivedRegression = config.yVariable.startsWith('derived_')
    let sensorDisplayName: string

    if (isDerivedRegression) {
      const derivedValue = derivedValues.find(
        (dv) => dv.id === config.yVariable
      )
      sensorDisplayName = derivedValue?.data.name || config.yVariable
    } else {
      const sensor = enhancedSensorEntries.find(
        (e) => e.id === config.yVariable
      )
      sensorDisplayName = sensor?.data?.name || config.yVariable
    }

    const sensorIndex = allSensorsToRender.findIndex(
      (s) => s.id === config.yVariable
    )
    if (sensorIndex === -1) return

    const lineColor = getSensorColor(sensorIndex)
    const scaleKey = singleScaleMode
      ? 'y'
      : sensorAxisMapping[config.yVariable]
        ? config.yVariable
        : 'y'

    if (
      xAxisSensorId &&
      config.regressionType === 'linear' &&
      config.xVariables?.length === 1 &&
      config.xVariables[0] === xAxisSensorId
    ) {
      result.push(
        buildXAxisRegressionColumn(
          config,
          regressionResult,
          sortedX,
          sensorDisplayName,
          lineColor,
          scaleKey
        )
      )
      return
    }

    if (!xAxisSensorId && config.xVariables[0] === 'timestamp') {
      const column = buildTimestampRegressionColumn(
        config,
        regressionResult,
        chartData,
        sortedX,
        sensorDisplayName,
        lineColor,
        scaleKey
      )
      if (column) result.push(column)
    }
  })

  return result
}
